/** Every wizard step, in order. Each is a route segment under `/onboarding`. */
export const ONBOARDING_STEPS = [
    'start',
    'accounting',
    'branch',
    'file',
    'names',
    'products',
    'result',
    'done',
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** The import steps the in-shell page shows: no Start, Branch or Done. */
export const SHELL_IMPORT_STEPS = [
    'file',
    'names',
    'products',
    'result',
] as const satisfies readonly OnboardingStep[];

export type StepStripMode = 'wizard' | 'shell';

export type StepState = 'done' | 'current' | 'upcoming' | 'skipped';

export type StepStripItem = {
    step: OnboardingStep;
    state: StepState;
    /** Accounting: run only when the organisation needs it. */
    isOptional: boolean;
};

export const isOnboardingStep = (
    value: string | undefined,
): value is OnboardingStep =>
    (ONBOARDING_STEPS as readonly string[]).includes(value ?? '');

export const stepsForMode = (mode: StepStripMode): readonly OnboardingStep[] =>
    mode === 'shell' ? SHELL_IMPORT_STEPS : ONBOARDING_STEPS;

type BuildStepStripInput = {
    mode: StepStripMode;
    /** `null` when the URL names no known step: every step reads upcoming. */
    currentStep: OnboardingStep | null;
    /** `accountingStartNeeded`; when false, Accounting is skipped. */
    accountingNeeded: boolean;
};

/**
 * Each step's state: earlier steps done, the current one current, later ones
 * upcoming, and Accounting skipped wherever it sits when the organisation
 * does not need it (a skipped step is never "done").
 */
export const buildStepStrip = ({
    mode,
    currentStep,
    accountingNeeded,
}: BuildStepStripInput): StepStripItem[] => {
    const steps = stepsForMode(mode);
    const currentIndex = currentStep === null ? -1 : steps.indexOf(currentStep);

    return steps.map((step, index) => {
        const isOptional = step === 'accounting';
        let state: StepState;
        if (isOptional && !accountingNeeded) {
            state = 'skipped';
        } else if (currentIndex === -1 || index > currentIndex) {
            state = 'upcoming';
        } else if (index === currentIndex) {
            state = 'current';
        } else {
            state = 'done';
        }
        return { step, state, isOptional };
    });
};

/**
 * The narrow strip's "n of m": the current step's place among the steps
 * that will actually run (skipped ones are not counted). `null` when no step
 * is current.
 */
export const stepPosition = (
    items: readonly StepStripItem[],
): { position: number; total: number } | null => {
    const counted = items.filter((item) => item.state !== 'skipped');
    const index = counted.findIndex((item) => item.state === 'current');
    return index === -1 ? null : { position: index + 1, total: counted.length };
};

/** The strip's links back: each linked step's address. */
export type StepStripLinks = Partial<Record<OnboardingStep, string>>;

type LinkableStepsInput = BuildStepStripInput & {
    /** An import is on its way: nothing moves until it answers. */
    isImporting: boolean;
    /** File is reading or checking a file: Cancel is its only action. */
    isFileBusy: boolean;
    /**
     * The organisation has an import that stands (not undone). Read on Done
     * only, where it decides whether Welcome is still offered.
     */
    hasCompletedImports: boolean;
};

/**
 * The steps the strip offers as links back: every step done before the
 * current one, except Accounting, which is only ever done because it ran
 * and cannot run again. Nothing on Result (the import is in: Undo, Import
 * another file and Next step are the ways on), and on Done only Welcome,
 * while no import stands (the Start fresh path). Nothing while an import
 * runs, or while File reads or checks a file. Never a step ahead. In
 * `shell` mode the same rule over File · Names · Products · Result.
 */
export const linkableSteps = ({
    isImporting,
    isFileBusy,
    hasCompletedImports,
    ...strip
}: LinkableStepsInput): ReadonlySet<OnboardingStep> => {
    const { currentStep } = strip;
    if (isImporting || isFileBusy || currentStep === 'result') {
        return new Set();
    }
    if (currentStep === 'done') {
        return new Set<OnboardingStep>(hasCompletedImports ? [] : ['start']);
    }
    return new Set(
        buildStepStrip(strip)
            .filter(
                (item) => item.state === 'done' && item.step !== 'accounting',
            )
            .map((item) => item.step),
    );
};
